import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNetwork } from '@/contexts/NetworkContext'
import { AuthService } from '@/services/core/auth'
import {
  ProductFeedApi,
  type Merchant,
  type RetailProduct,
} from '@/services/productFeed/productFeedApi'
import {
  NO_RETAIL_PRODUCT_FILTERS,
  type RetailProductFilterState,
} from '@/components/RetailProductFilters'

/** The facets change only when a feed is imported again. */
const FACETS_STALE_TIME = 10 * 60 * 1000

/** Shared, so the table's columns are not rebuilt while the facets load. */
const NO_MERCHANTS: Merchant[] = []

/**
 * The state behind a server-paged retail product list: its page, filters,
 * selection and the products they match. Selected products are kept across
 * pages, so a selection can span several of them.
 */
export function useRetailProductList(search: string, enabled = true) {
  const { scopeKey, scopeReady } = useNetwork()
  const ready = enabled && AuthService.isAuthenticated() && scopeReady
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [filters, setFilters] = useState<RetailProductFilterState>(NO_RETAIL_PRODUCT_FILTERS)
  const [selection, setSelection] = useState<RetailProduct[]>([])

  const { data: facets, isLoading: facetsLoading } = useQuery({
    queryKey: ['productfeed', 'facets', scopeKey],
    queryFn: () => ProductFeedApi.getFacets(),
    enabled: ready,
    staleTime: FACETS_STALE_TIME,
  })

  const { data, isLoading } = useQuery({
    queryKey: ['productfeed', 'page', scopeKey, search, filters, page, pageSize],
    queryFn: () =>
      ProductFeedApi.getPage(
        {
          search,
          merchantId: filters.merchantId ? Number(filters.merchantId) : undefined,
          brand: filters.brand,
          colour: filters.colour,
        },
        page,
        pageSize
      ),
    enabled: ready,
  })
  const products = data?.results ?? []

  /**
   * The table only reports the rows on the page it shows, so picks made on
   * other pages are carried over from the current selection.
   */
  const changeSelection = (onPage: RetailProduct[]) => {
    const pageKeys = new Set(products.map((p) => p.key))
    setSelection([...selection.filter((p) => !pageKeys.has(p.key)), ...onPage])
  }

  /** Back to the first page whenever the rows underneath change. */
  const refilter = (apply: () => void) => {
    apply()
    setPage(1)
  }

  return {
    facets,
    facetsLoading,
    merchants: facets?.merchants ?? NO_MERCHANTS,
    products,
    totalCount: data?.totalCount ?? 0,
    loading: isLoading,
    page,
    setPage,
    pageSize,
    setPageSize: (size: number) => refilter(() => setPageSize(size)),
    filters,
    setFilters: (next: RetailProductFilterState) => refilter(() => setFilters(next)),
    refilter,
    selection,
    changeSelection,
    clearSelection: () => setSelection([]),
  }
}
